import {
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTheme,
  defaultTypography,
  GSActionBar,
  GSEmptyList,
  GSItemList,
  useMediaQuery,
} from "golfstatus_react_components";
import { getRoundName } from "../../../hooks/round/roundHooks";
import { useDispatch, useSelector } from "react-redux";
import {
  faArrowRight,
  faBolt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { selectCurrentTournament } from "../../../reducers/tournamentSlice";
import TournamentTeamCheckInItem from "../tournament-teams/tournament-team-check-in-item";
import {
  checkInPlayer,
  checkInTeam,
  checkOutTeam,
  selectNotifications,
  checkOutPlayer,
} from "../../../reducers/checkinSlice";
import { activateScorecards } from "../../../reducers/roundSlice";

//api should be sorting now, but leaving this incase we want to do front end sorting later.
export const roundSort = (a, b) => {
  const check =
    new Date(a.holeAssignment.teeTime) - new Date(b.holeAssignment.teeTime) ||
    a.holeAssignment?.hole - b.holeAssignment?.hole ||
    a.holeAssignment?.position?.localeCompare(b.holeAssignment?.position) ||
    a.holeAssignment?.subPosition - b.holeAssignment?.subPosition;
  return check;
};

const TournamentRoundsCheckinRound = (props) => {
  const { round, offset, setRound, setSearch } = props;

  const filteredTeams = round?.checkIns?.mappedCheckins ?? [];
  const playerCheckins = round?.checkIns?.playerCheckins
  const checkedInPlayers = playerCheckins?.filter?.(pci => !!pci.checkedInAt)

  const {isPhone} = useMediaQuery()

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const tournament = useSelector(selectCurrentTournament);

  const pendingNotifications = useSelector(selectNotifications)?.filter?.(
    (n) => n.result === "pending",
  );

  const getEmptyList = () => {
    if (round.state === "draft") {
      return getDraftEmptyList();
    }

    if (round.state === "ready") {
      return getReadyEmptyList();
    }

    return getDefaultEmptyList();
  };

  const getReadyEmptyList = () => {
    return (
      <GSEmptyList
        style={{ ...defaultPadding.smallLayoutPad.apply("vertical") }}
        title="This round has to be live to check teams in..."
        detail="Please check your hole assignments or start the round"
        actions={[
          {
            title: "View Hole Assignments",
            type: "grey",
            onClick: () => {
              navigate(`/${tournament.id}/rounds/${round.id}/hole-assignments`);
            },
          },
          {
            title: "Start Round",
            type: "green",
            onClick: () => {
              navigate(`/${tournament.id}/rounds/${round.id}/scoring`);
            },
          },
        ]}
      />
    );
  };

  const getDraftEmptyList = () => {
    return (
      <GSEmptyList
        style={{ ...defaultPadding.smallLayoutPad.apply("vertical") }}
        title="No Hole Assignments Yet"
        detail="To check in teams, complete hole assignments and start the round."
        actions={[
          {
            title: "Hole Assignments",
            buttonIcon: faBolt,
            type: "grey",
            onClick: () => {
              navigate(`/${tournament.id}/rounds/${round.id}/hole-assignments`);
            },
          },
        ]}
      />
    );
  };

  const getDefaultEmptyList = () => {
    return (
      <GSEmptyList
        style={{ ...defaultPadding.smallLayoutPad.apply("vertical") }}
        title="We couldn't find who you were looking for..."
        detail="Try changing the search term or search all rounds."
        actions={[
          {
            title: "Clear Search",
            type: "grey",
            onClick: () => {
              setSearch("");
            },
          },
          {
            title: "Search All Rounds",
            buttonIcon: faSearch,
            type: "grey",
            onClick: () => {
              setRound?.(null);
            },
          },
        ]}
      />
    );
  };

  const getDescription = () => {
    const direction = isPhone() ? {...defaultLayouts.vStack} : {...defaultLayouts.hStack, ...defaultGapping.mediumGap}
    if (round.state === "live") {
      return <div style={{...direction}}>
        <div>{`${round?.checkIns?.teamsCheckedIn}/${round?.checkIns?.totalTeams} Teams Checked In`}</div>
        {!isPhone() ? <div>|</div>:null}
        <div>{`${checkedInPlayers?.length}/${playerCheckins?.length} Players Checked In`}</div>
      </div>;
    }
    if (round.state === "draft") {
      return "";
    }
    if (round.state === "final") {
      return "This round has been finalized.";
    }
    return "To check in teams, start the round.";
  };

  const roundHeader = () => {
    return (
      <div style={{ ...defaultLayouts.vStack }}>
        <div>{`Round ${getRoundName(round)}`}</div>
        <div style={{ ...defaultTypography.bodyRegular.withOpacity(0.7) }}>
          {getDescription()}
        </div>
      </div>
    );
  };

  const getRoundActions = () => {
    const actions = [
      {
        buttonIcon: faArrowRight,
        actionClick: () => {
          navigate(`/${tournament.id}/rounds/${round.id}`);
        },
      },
    ];
    if (round.state === "ready") {
      actions.unshift({
        title: "Start Round",
        type: "green no-wrap",
        actionClick: () => {
          dispatch(activateScorecards(round.id));
        },
      });
    }

    return actions;
  };

  const teamCheckIn = (checkIn) => {
    if (checkIn.id) {
      dispatch(checkInTeam(checkIn.id));
    }
  };

  const teamCheckOut = (checkIn) => {
    if (checkIn.id) {
      dispatch(checkOutTeam(checkIn.id));
    }
  };

  const playerCheckIn = (checkIn) => {
    if (checkIn.id) {
      dispatch(checkInPlayer(checkIn.id));
    }
  };

  const playerCheckOut = (checkIn) => {
    if (checkIn.id) {
      dispatch(checkOutPlayer(checkIn.id));
    }
  };

  return (
    <tournament-rounds-checkin-round
      style={{ ...defaultLayouts.vStack, ...defaultLayouts.fullWidth }}
    >
      <GSActionBar
        header={roundHeader()}
        style={{
          ...defaultPadding.xLargePad,
          ...defaultTheme.surfaceVariant.light,
          ...defaultLayouts.fullWidth,
          ...defaultLayouts.stickyTop,
          top: offset ?? 0,
          zIndex: 1,
        }}
        pageActions={getRoundActions()}
      />

      <GSItemList
        type="vertical"
        items={filteredTeams}
        emptyMessage={getEmptyList()}
        listItem={(item) => {
          return (
            <div
              style={{
                ...defaultLayouts.fullWidth,
                borderBottom: "1px solid #eee",
                ...defaultPadding.xLargePad,
                ...defaultLayouts.hStack.align("space-between", "center"),
              }}
            >
              <TournamentTeamCheckInItem
                pending={pendingNotifications}
                isDisabled={round?.state !== "live"}
                style={{ ...defaultLayouts.fullWidth }}
                {...item}
                checkInClick={teamCheckIn}
                checkOutClick={teamCheckOut}
                checkPlayerInClick={playerCheckIn}
                checkPlayerOutClick={playerCheckOut}
              />
            </div>
          );
        }}
      />
    </tournament-rounds-checkin-round>
  );
};

export default TournamentRoundsCheckinRound;
